import { useEffect, useLayoutEffect, useMemo, useRef, useState, type RefObject } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { checkoutSeats, fetchSeatMap } from '../api/endpoints';
import { subscribeSession, type SeatEvent } from '../api/sse';
import { haptic, hapticNotify, lockMiniAppViewport, showAlert, unlockMiniAppViewport } from '../lib/tg';
import { formatApiError } from '../lib/errors';
import { useSeatStore } from '../store/seatStore';
import { formatUZS } from '../lib/format';
import { usePinchZoom } from '../hooks/usePinchZoom';
import TopBar from '../components/TopBar';
import Splash from '../components/Splash';
import type { SeatMapSeat, SeatStatus } from '../types/models';

const MAX_SEATS = 10;

// Visual cell sizing
const CELL = 22;
const GAP = 4;
// Editor stores pos_x / pos_y at its own cell scale (CELL=16, GAP=3 → stride 19).
// Scale positions when rendering so sectors don't overlap at the larger
// player-cell scale.
const EDITOR_STRIDE = 16 + 3;
const PLAYER_STRIDE = CELL + GAP;
const POS_SCALE = PLAYER_STRIDE / EDITOR_STRIDE;

const MAX_ZOOM = 2.5;
const ZOOM_STEP = 0.2;
// Hard floor regardless of fit-to-screen: never let the user shrink below
// readable seat-tap size.
const ABS_MIN_ZOOM = 0.4;

export default function SeatMap() {
  const { t } = useTranslation();
  const { id } = useParams();
  const sessionId = Number(id);
  const navigate = useNavigate();
  const qc = useQueryClient();

  // Local-only selection. Seats are not held on the server until the user
  // taps 'go to payment' — eliminates the per-tap network round-trip that
  // sometimes failed and avoids parking a hold the user might abandon.
  const [selectedSeatIds, setSelectedSeatIds] = useState<number[]>([]);
  const [liveOverrides, setLiveOverrides] = useState<Record<number, SeatStatus>>({});
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const outerRef = useRef<HTMLDivElement | null>(null);
  const innerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    // Reset on session change.
    setSelectedSeatIds([]);
    useSeatStore.getState().reset();
  }, [sessionId]);

  // Lock vertical swipes so panning the seat map doesn't drag the Mini App sheet.
  useEffect(() => {
    lockMiniAppViewport();
    return () => unlockMiniAppViewport();
  }, []);

  const { data, isLoading } = useQuery({
    queryKey: ['seatmap', sessionId],
    queryFn: () => fetchSeatMap(sessionId),
    enabled: !!sessionId,
    refetchOnMount: 'always',
  });

  // SSE subscription
  useEffect(() => {
    if (!sessionId) return;
    const unsub = subscribeSession(sessionId, (ev: SeatEvent) => {
      setLiveOverrides((prev) => {
        const next = { ...prev };
        if (ev.type === 'seat_released') next[ev.seat_id] = 'free';
        else if (ev.type === 'seat_held') next[ev.seat_id] = 'held';
        else if (ev.type === 'seat_paid') next[ev.seat_id] = 'paid';
        return next;
      });
      // If someone else grabbed a seat I'd selected, drop it from selection.
      if (ev.type === 'seat_held' || ev.type === 'seat_paid') {
        setSelectedSeatIds((cur) => cur.filter((sid) => sid !== ev.seat_id));
      }
    });
    return unsub;
  }, [sessionId]);

  const seatsById = useMemo(() => {
    const m = new Map<number, SeatMapSeat>();
    data?.seats.forEach((s) => m.set(s.id, s));
    return m;
  }, [data]);

  const total = useMemo(() => {
    let sum = 0;
    selectedSeatIds.forEach((id) => { sum += seatsById.get(id)?.price ?? 0; });
    return sum;
  }, [selectedSeatIds, seatsById]);

  function normalize(raw: string | undefined): SeatStatus {
    // Backend sends FREE / HELD / TAKEN (uppercase). Internal UI status is
    // lowercase: 'free' | 'mine' | 'held' | 'paid' | 'used'. We collapse
    // TAKEN → 'paid' since both mean "not selectable" with the dimmed look.
    switch ((raw || '').toUpperCase()) {
      case 'TAKEN':
        return 'paid';
      case 'HELD':
        return 'held';
      case 'PAID':
        return 'paid';
      case 'USED':
        return 'used';
      default:
        return 'free';
    }
  }

  function resolveStatus(seat: SeatMapSeat): SeatStatus {
    if (selectedSeatIds.includes(seat.id)) return 'mine';
    const override = liveOverrides[seat.id];
    if (override) return override;
    return normalize(seat.status as unknown as string);
  }

  function toggleSeat(seat: SeatMapSeat) {
    const status = resolveStatus(seat);
    if (status === 'held' || status === 'paid' || status === 'used') return;
    if (status === 'mine') {
      haptic('light');
      setSelectedSeatIds((cur) => cur.filter((sid) => sid !== seat.id));
      return;
    }
    if (selectedSeatIds.length >= MAX_SEATS) {
      hapticNotify('warning');
      showAlert(t('seats.limit'));
      return;
    }
    haptic('medium');
    setSelectedSeatIds((cur) => [...cur, seat.id]);
  }

  const checkoutMut = useMutation({
    mutationFn: () => checkoutSeats(sessionId, selectedSeatIds),
    onSuccess: (res) => {
      hapticNotify('success');
      useSeatStore.getState().setContext(sessionId);
      // Record the just-created order so /payment can pick it up via store
      // if needed (legacy code paths). The Payment page also re-fetches.
      res.held_seats.forEach((sid) => {
        useSeatStore.getState().add(
          sid,
          res.order_id,
          new Date(Date.now() + res.hold_ttl * 1000).toISOString(),
        );
      });
      navigate(`/payment/${res.order_id}`);
    },
    onError: async (err: any) => {
      hapticNotify('error');
      const detail = err?.response?.data?.detail;
      // Backend signals seat conflicts with a structured detail.
      const conflicts: number[] | undefined = detail?.conflicts;
      if (Array.isArray(conflicts) && conflicts.length) {
        setSelectedSeatIds((cur) => cur.filter((sid) => !conflicts.includes(sid)));
        setLiveOverrides((prev) => {
          const next = { ...prev };
          conflicts.forEach((sid) => (next[sid] = 'held'));
          return next;
        });
        await showAlert('Часть мест уже заняли. Они убраны из выбора — посмотрите карту и выберите снова.');
        qc.invalidateQueries({ queryKey: ['seatmap', sessionId] });
        return;
      }
      await showAlert(formatApiError(err, t('common.error')));
    },
  });

  // Bounding box of all sectors for canvas size — must be computed before
  // early return so the effect below can use it.
  const bbox = data ? sectorBBox(data.sectors) : { width: 0, height: 0, minX: 0, minY: 0 };
  const canvasW = bbox.width + 40;
  const canvasH = bbox.height + 40;

  const [zoom, setZoom] = useState(1);
  // Fit-to-screen minimum: the user shouldn't be able to zoom out further than
  // "every sector visible at once" — going beyond just shrinks pixels for no
  // useful information.
  const [minZoom, setMinZoom] = useState(ABS_MIN_ZOOM);
  const pinchingRef = useRef({ current: false });

  // Recompute fit-to-screen min zoom whenever the canvas or viewport changes,
  // and snap an initial zoom that fits.
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el || !data) return;
    const fit = Math.min(el.clientWidth / canvasW, el.clientHeight / canvasH);
    const floor = Math.max(ABS_MIN_ZOOM, Math.min(1, fit));
    setMinZoom(floor);
    setZoom((z) => Math.max(floor, Math.min(MAX_ZOOM, z)));
  }, [data, canvasW, canvasH]);

  usePinchZoom({
    container: scrollRef as RefObject<HTMLDivElement>,
    outer: outerRef as RefObject<HTMLDivElement>,
    inner: innerRef as RefObject<HTMLDivElement>,
    baseWidth: canvasW,
    baseHeight: canvasH,
    zoom,
    setZoom: (z) => setZoom(+z.toFixed(3)),
    minZoom,
    maxZoom: MAX_ZOOM,
    pinchingRef,
  });

  // Center the seat map horizontally on first render.
  useEffect(() => {
    if (!data || !scrollRef.current) return;
    const el = scrollRef.current;
    requestAnimationFrame(() => {
      el.scrollLeft = Math.max(0, (canvasW * zoom - el.clientWidth) / 2);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  if (isLoading || !data) return <Splash />;

  return (
    <div className="min-h-screen pb-40">
      <TopBar title={t('seats.title')} />

      <div className="mx-4 mt-3 mb-2 h-2 rounded-full bg-gradient-to-b from-accent/40 to-transparent" />
      <div className="text-center text-xs text-muted mb-2">{t('seats.screen')}</div>

      <div className="flex items-center justify-end gap-2 px-3 pb-2">
        <button
          className="px-3 py-1.5 rounded-full bg-bg-elev text-sm disabled:opacity-50"
          disabled={zoom <= minZoom + 1e-3}
          onClick={() => setZoom((z) => Math.max(minZoom, +(z - ZOOM_STEP).toFixed(2)))}
          aria-label="zoom out"
        >
          −
        </button>
        <div className="text-xs text-muted w-10 text-center">{Math.round(zoom * 100)}%</div>
        <button
          className="px-3 py-1.5 rounded-full bg-bg-elev text-sm disabled:opacity-50"
          disabled={zoom >= MAX_ZOOM - 1e-3}
          onClick={() => setZoom((z) => Math.min(MAX_ZOOM, +(z + ZOOM_STEP).toFixed(2)))}
          aria-label="zoom in"
        >
          +
        </button>
        <button
          className="px-3 py-1.5 rounded-full bg-bg-elev text-sm"
          onClick={() => setZoom(minZoom)}
          aria-label="fit to screen"
        >
          ⤢
        </button>
      </div>

      <div
        className="overflow-auto no-drag-select mx-3 rounded-xl2"
        ref={scrollRef as any}
        style={{ height: 'calc(100dvh - 280px)', touchAction: 'pan-x pan-y' }}
        onContextMenu={(e) => e.preventDefault()}
      >
        <div
          ref={outerRef}
          // Outer wrapper holds scaled size so the scroll container can pan.
          // Width/height are mutated imperatively during pinch for smoothness.
          style={{ width: canvasW * zoom, height: canvasH * zoom }}
        >
        <div
          ref={innerRef}
          className="relative"
          style={{
            width: canvasW,
            height: canvasH,
            padding: 20,
            transform: `scale(${zoom})`,
            transformOrigin: 'top left',
          }}
        >
          {data.sectors.map((sec) => {
            const seatsHere = data.seats.filter((s) => s.sector_id === sec.id);
            return (
              <div
                key={sec.id}
                className="absolute"
                style={{
                  left: (sec.pos_x - bbox.minX) * POS_SCALE + 20,
                  top: (sec.pos_y - bbox.minY) * POS_SCALE + 20,
                }}
              >
                <div className="text-[10px] text-muted mb-1 px-1">{sec.name}</div>
                <div
                  className="grid"
                  style={{
                    gridTemplateColumns: `repeat(${sec.seats_per_row}, ${CELL}px)`,
                    gap: GAP,
                  }}
                >
                  {seatsHere
                    .slice()
                    .sort((a, b) => a.row_num - b.row_num || a.seat_num - b.seat_num)
                    .map((seat) => {
                      const status = resolveStatus(seat);
                      return (
                        <SeatCell
                          key={seat.id}
                          status={status}
                          color={sec.color}
                          onClick={() => toggleSeat(seat)}
                        />
                      );
                    })}
                </div>
              </div>
            );
          })}
        </div>
        </div>
      </div>

      <Legend />

      <div className="fixed bottom-0 inset-x-0 p-4 bg-bg/95 backdrop-blur border-t border-line">
        <div className="flex items-center justify-between mb-2.5">
          <div className="text-sm text-muted">{t('seats.selected', { count: selectedSeatIds.length })}</div>
          <div className="font-semibold">{formatUZS(total)} <span className="text-muted text-sm">{t('common.sum')}</span></div>
        </div>
        <button
          className="btn-primary"
          disabled={selectedSeatIds.length === 0 || checkoutMut.isPending}
          onClick={() => checkoutMut.mutate()}
        >
          {checkoutMut.isPending ? '…' : t('seats.pay')}
        </button>
      </div>
    </div>
  );
}

function sectorBBox(sectors: { pos_x: number; pos_y: number; seats_per_row: number; rows_count: number }[]) {
  if (!sectors.length) return { width: 200, height: 200, minX: 0, minY: 0 };
  const xs = sectors.map((s) => s.pos_x);
  const ys = sectors.map((s) => s.pos_y);
  const minX = Math.min(...xs);
  const minY = Math.min(...ys);
  const xMax = Math.max(
    ...sectors.map((s) => (s.pos_x - minX) * POS_SCALE + s.seats_per_row * PLAYER_STRIDE),
  );
  const yMax = Math.max(
    ...sectors.map((s) => (s.pos_y - minY) * POS_SCALE + s.rows_count * PLAYER_STRIDE + 16),
  );
  return { width: xMax, height: yMax, minX, minY };
}

function SeatCell({
  status,
  color,
  onClick,
}: {
  status: SeatStatus;
  color: string;
  onClick: () => void;
}) {
  let bg = color;
  let border = 'transparent';
  let opacity = 1;
  if (status === 'mine') {
    border = '#fff';
  } else if (status === 'held' || status === 'paid' || status === 'used') {
    bg = '#3a3550';
    opacity = 0.6;
  }
  return (
    <button
      onClick={onClick}
      disabled={status === 'held' || status === 'paid' || status === 'used'}
      style={{
        width: CELL,
        height: CELL,
        background: bg,
        borderRadius: 5,
        border: `2px solid ${border}`,
        opacity,
      }}
      className="transition active:scale-90"
      aria-label="seat"
    />
  );
}

function Legend() {
  const { t } = useTranslation();
  return (
    <div className="flex justify-center gap-4 mt-4 text-xs text-muted">
      <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-accent" /> {t('seats.free')}</span>
      <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-accent border-2 border-white" /> {t('seats.mine')}</span>
      <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-[#3a3550]" /> {t('seats.taken')}</span>
    </div>
  );
}
